import { useCallback, useMemo, type RefObject } from "react";
import { toBlob } from "html-to-image";
import {
  useSnapshot as useSnapshotBase,
  type UseSnapshotResult,
} from "@/lib/use-snapshot";
import type { PlacementMap } from "./use-card-placement";

/**
 * StressTest snapshot wrapper. The stress-test board is DOM-only
 * (cards + zones are `div`s), so we delegate the rasterisation to
 * `html-to-image`'s `toBlob`. That handles the inline styling we
 * already lean on (CSS variables, the spectrum gradient, the zone
 * washes) without us having to reinvent a renderer.
 *
 * The PNG is what the facilitator sees on screen — but a PNG can't
 * be reversed back into card → zone mappings, so we *also* stash the
 * live `PlacementMap` as `stateJson`. Restore writes the map straight
 * back through `replaceAll`, and the existing placement persistence
 * effect mirrors it to idb-keyval.
 */

type Options = {
  workshopId: string;
  /** Live ref to the outer board container that should be rasterised. */
  boardRef: RefObject<HTMLDivElement | null>;
  /** Current placements — captured into `stateJson`. */
  placements: PlacementMap;
  /** Apply a restored placement map to the live board. */
  applyRestoredPlacements: (next: PlacementMap) => void;
};

/**
 * Resolve a CSS variable to its concrete RGB value by routing it
 * through a hidden helper element. `html-to-image` honours computed
 * styles, but the backgroundColor option we pass into `toBlob` must
 * be a concrete value — variable references are read against the
 * snapshot root which has no inherited theme.
 */
function resolveCssColor(input: string): string {
  const helper = document.createElement("span");
  helper.style.display = "none";
  document.body.appendChild(helper);
  helper.style.color = input;
  const computed = getComputedStyle(helper).color;
  helper.remove();
  return computed || input;
}

async function captureStressTestBlob(
  boardRef: RefObject<HTMLDivElement | null>,
): Promise<Blob | null> {
  const node = boardRef.current;
  if (!node) return null;
  const bg = resolveCssColor("var(--bg-panel)") || "#ffffff";
  try {
    const blob = await toBlob(node, {
      pixelRatio: 2,
      backgroundColor: bg,
      // `cacheBust` keeps the DOM-to-canvas pipeline honest if the
      // board references any inline-svg icons — without it lucide
      // glyphs occasionally render stale.
      cacheBust: true,
    });
    return blob ?? null;
  } catch {
    return null;
  }
}

export function useStressTestSnapshot(opts: Options): UseSnapshotResult {
  const { workshopId, boardRef, placements, applyRestoredPlacements } = opts;

  const captureBlob = useCallback(
    () => captureStressTestBlob(boardRef),
    [boardRef],
  );

  const captureStateJson = useCallback(() => {
    try {
      return JSON.stringify(placements);
    } catch {
      return undefined;
    }
  }, [placements]);

  const restoreFromSnapshot = useCallback(
    async (meta: { stateJson?: string }) => {
      if (!meta.stateJson) return;
      try {
        const parsed = JSON.parse(meta.stateJson) as PlacementMap;
        applyRestoredPlacements(parsed);
      } catch {
        // Malformed snapshot — bail rather than clobber the live map.
      }
    },
    [applyRestoredPlacements],
  );

  return useSnapshotBase(
    useMemo(
      () => ({
        workshopId,
        kind: "stress-test" as const,
        captureBlob,
        captureStateJson,
        restoreFromSnapshot,
      }),
      [workshopId, captureBlob, captureStateJson, restoreFromSnapshot],
    ),
  );
}
